import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, computed,
  DestroyRef, ElementRef, EventEmitter, Input, OnInit, Output,
  signal, viewChild, viewChildren, ViewEncapsulation, inject, DOCUMENT,
} from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { NgTemplateOutlet } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';

import { MatTooltip, MatTooltipModule } from '@angular/material/tooltip';
import { MatTree, MatTreeModule } from '@angular/material/tree';
import { IrisMenuItemI } from '@iris/common/models/IrisMenuItem';
import { MenuItemNode, MenuItemNodeI } from '@iris/common/modules/global-navigation-menu/models/menu-item-node';
import { IrisGlobalSandbox } from '@iris/common/redux/global.sandbox';
import { IrisInfrastructureService } from '@iris/common/services/infrastructure.service';
import { IrisMainMenuService } from '@iris/common/services/main-menu.service';
import { IrisLocalStorageService } from '@iris/common/services/utils/iris-local-storage.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Hotkey, HotkeysService } from 'angular2-hotkeys';
import isNil from 'lodash/isNil';
import { combineLatest, EMPTY, of, Subject, timer } from 'rxjs';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { catchError, debounceTime, filter, map, share, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { MatDrawerMode } from '@angular/material/sidenav';
import { EmailsWebsocketService } from '@iris/common/services/websockets/emails-websocket.service';
import { IrisEmailsGlobalSandbox } from '@iris/common/modules/emails/sandbox/global/emails-global.sandbox';
import { IrisAnnouncementsService } from '@iris/modules/administration/administration-list/announcement-page/services/announcements.service';
import { IrisTranslationPipe } from '@iris/common/modules/pipes-common/pipes/translate/translation.pipe';
import { AnnouncementNewFunctionalityConfig } from '@iris/modules/administration/administration-list/announcement-page/models/announcement-models';
import { IrisEmailsSandboxModule } from '@iris/common/modules/emails/sandbox/emails-sandbox.module';
import { IrisNewFunctionalityButtonComponent } from '../iris-new-functionality-button/iris-new-functionality-button.component';
import {
  StrakitTextComponent,
  StrakitToggleComponent,
} from '@ngx-strakit/cdk/controls';
import { AppAliasUrlSerializer } from '@iris/common/services/app-alias-url-serializer';

const NAVIGATION_MENU_SEARCH_THRESHOLD = 50;

interface HighlightedTitlePart {
  text: string;
  match: boolean;
}

@Component({
  selector: 'global-navigation-menu',
  imports: [
    TranslatePipe,
    RouterLink,
    FormsModule,
    ReactiveFormsModule,
    MatTreeModule,
    MatTooltipModule,
    IrisEmailsSandboxModule,
    IrisNewFunctionalityButtonComponent,
    StrakitTextComponent,
    StrakitToggleComponent,
    NgTemplateOutlet,
  ],
  templateUrl: './global-navigation-menu.component.html',
  styleUrls: ['./global-navigation-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('slideVertical', [
      state('*', style({
        height: 0,
      })),
      state('show', style({
        height: '*',
      })),
      transition('* => *', [animate('400ms cubic-bezier(0.25, 0.8, 0.25, 1)')]),
    ]),
  ],
})
export class IrisGlobalNavigationMenuComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly changeDetector = inject(ChangeDetectorRef);
  private readonly storage = inject(IrisLocalStorageService);
  private readonly menuService = inject(IrisMainMenuService);
  private readonly translate = inject(TranslateService);
  private readonly irisTranslation = inject(IrisTranslationPipe);
  private readonly hotkeysService = inject(HotkeysService);
  private readonly infrastructureService = inject(IrisInfrastructureService);
  private readonly globalSandbox = inject(IrisGlobalSandbox);
  private readonly emailsGlobalSandbox = inject(IrisEmailsGlobalSandbox);
  private readonly emailsWebsocketService = inject(EmailsWebsocketService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly irisAnnouncementsService = inject(IrisAnnouncementsService);
  private readonly urlSerializer = inject(AppAliasUrlSerializer);
  private readonly document = inject<Document>(DOCUMENT);

  readonly scrollBox = viewChild<ElementRef<HTMLElement>>('scrollBox');
  readonly tooltips = viewChildren(MatTooltip);

  readonly matTree = viewChild(MatTree);
  @Input() set sidenavMode(mode: MatDrawerMode) {
    this._sidenavMode = mode;
    this.pinned = mode === 'side';
    this.hasBackdrop = !this.pinned;
  }

  get sidenavMode(): MatDrawerMode {
    return this._sidenavMode;
  }
  private _sidenavMode: MatDrawerMode;

  @Output() sidenavModeChange = new EventEmitter<MatDrawerMode>();

  readonly navigationMenu = signal<MenuItemNodeI[]>([]);
  readonly searchControl = new FormControl<string>('');
  readonly searchTerm$ = this.searchControl.valueChanges.pipe(
    map(value => value ?? ''),
    share(),
  );
  readonly searchTerm = toSignal(this.searchTerm$, { initialValue: '' });
  readonly showNavigationMenuSearch = computed(() => {
    return this.countMenuItems(this.navigationMenu()) > NAVIGATION_MENU_SEARCH_THRESHOLD;
  });
  readonly filteredNavigationMenu = computed(() => {
    const navigationMenu = this.navigationMenu();
    const searchTerm = this.searchTerm().trim().toLowerCase();

    if (!this.showNavigationMenuSearch() || !searchTerm) {
      return navigationMenu;
    }

    return this.filterMenuItems(navigationMenu, searchTerm);
  });

  readonly childrenAccessor = (node: MenuItemNodeI): MenuItemNodeI[] => node.children ?? [];
  pinned: boolean;
  hasBackdrop: boolean;
  public readonly emailUnreadMessagesCount = this.emailsGlobalSandbox.unreadCount;

  private readonly delayBeforeOpen = 2000;

  private readonly menuChangedSubject = new Subject<number>();
  private readonly titleOnlySearchMatches = new WeakSet<MenuItemNodeI>();

  private timeoutIdOnMouseEnter: number;
  private timeoutIdOnMouseLeave: number;
  private openedOnMouseEnter = false;

  constructor() {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),

      tap((event: NavigationEnd) => {
        if (!event.url?.length || !this.navigationMenu()?.length) { return; }

        const currentUrl = this.document.location?.pathname ?? `/${this.globalSandbox.appAlias()}${event.url}`;

        const isLeftMenuItemChosen = this.navigationMenu().some((menuItem) => {
          if (menuItem.children?.length) {
            return menuItem.children.some((subItem) => currentUrl.startsWith(subItem.url));
          }
          return currentUrl.startsWith(menuItem.url);
        });

        if (isLeftMenuItemChosen) { return; }

        this._deactivateMenuItems(this.navigationMenu());
        this.changeDetector.markForCheck();
      }),

      tap(() => {
        const currentPageUrl = this.document.location.pathname;
        if (this.isSidebarOpened() && !this.pinned && this.isSidebarOver()) {
          void this.menuService.sidenav.close();
        }
        this.setActiveItem(this.navigationMenu(), currentPageUrl);
        this.changeDetector.markForCheck();
      }),
      debounceTime(1000),
      tap(() => this.scrollToActiveItem()),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();

    this.hotkeysService.add([
      new Hotkey(['up', 'down'], (event: KeyboardEvent): boolean => {
        return !this.navigate(event.code === 'ArrowDown' ? 1 : -1);
      }),
    ]);

    this.searchTerm$.pipe(
      switchMap(value => value.trim() ? timer(0) : EMPTY),
      tap(() => {
        this.filteredNavigationMenu().forEach(node => {
          this.expandFilteredNode(node);
        });
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();
  }

  ngOnInit(): void {
    this.handleEmailClientInboxCounter();

    combineLatest([
      this.menuChangedSubject,
      this.globalSandbox.appAlias$,
    ]).pipe(
      switchMap(([projectId]) => {
        return combineLatest([
          this.menuService.getLeftMenu(projectId).pipe(catchError((_e: Error) => of([] as IrisMenuItemI[]))),
          this.irisAnnouncementsService.getAnnouncementConfigs(true).pipe(
            catchError(() => of([] as AnnouncementNewFunctionalityConfig[])),
          ),
        ]).pipe(
          map(([menu, activeAnnouncementConfigs]) => {
            activeAnnouncementConfigs.forEach(config => {
              let template: IrisMenuItemI;
              menu.filter(item => item.moduleType === 'DYNAMIC_FORMS').forEach(item => {
                template = item.children.find(item => item.url.endsWith(`/${config.documentTemplateId}`));
                if (template) {
                  template.newFunctionalityConfig = config;
                }
              });
            });

            return menu;
          }),
        );
      }),
      tap((menu) => {
        if (menu.length === 0) {
          this.navigationMenu.set([]);
        } else {
          const navigationMenu = menu
            .filter(menuItem => menuItem.showInQuickNavigation)
            .map(menuItem =>
              MenuItemNode.fromMenuItem(menuItem, (item) => this.getTitle(item)),
            );

          this.navigationMenu.set(navigationMenu);

          // After reloading the page, we need to expand all the menus that have been expanded before
          navigationMenu.forEach(node => {
            if (this.isExpanded(node) && !this.matTree()?.isExpanded(node)) {
              this.toggleSubMenu(node);
            }
          });

          const currentPageUrl = this.document.location.pathname;
          this.setActiveItem(navigationMenu, currentPageUrl);
        }
      }),
      // Timer for navigationMenu view initialization
      debounceTime(1000),
      tap(() => this.scrollToActiveItem()),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();

    this.globalSandbox.currentProjectId$.pipe(
      tap((projectId) => this.menuChangedSubject.next(projectId)),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();

    this.menuService.listenLeftMenuUpdateByProjects().pipe(
      withLatestFrom(this.globalSandbox.currentProjectId$),
      tap(([updatedProjectIds, currentProjectId]) => {
        if (currentProjectId && updatedProjectIds.includes(currentProjectId)) {
          this.menuChangedSubject.next(currentProjectId);
        }
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();
  }

  isSidebarOpened(): boolean {
    return this.menuService.sidenav?.opened;
  }

  showTooltipIfNeeded(item: MenuItemNode): string {
    return !this.isSidebarOpened() || item.$title.length > 24 ? item.$title : '';
  }

  public close(): void {
    if (this.isSidebarOver()) {
      this.clearMouseTimeouts();
      void this.menuService.sidenav.close();
    }
  }

  public isSidebarOver(): boolean {
    return this.sidenavMode === 'over';
  }

  private getTitle(menuItem: IrisMenuItemI): string {
    if (menuItem.moduleType.includes('IRIS_MODULE')) {
      return this.irisTranslation.transform(
        this.translate.instant(menuItem.shortTitle || menuItem.title),
        menuItem.translations,
        'name',
      );
    }

    return menuItem.nameTranslated;
  }

  public hasChild(_: number, node: MenuItemNode): boolean {
    return !!node.children && node.children.length > 0;
  }

  scrollToActiveItem(): void {
    const activeElements = this.document.querySelectorAll(`.iris-menu-list .menu-item-container.active`);
    const currentElement = activeElements[activeElements.length - 1];

    if (activeElements?.length) {
      if (this.infrastructureService.isTablet) {
        const safeOffset = 140;
        const scrollElement = this.scrollBox().nativeElement;
        const relativeElementTop = currentElement.getBoundingClientRect().top;
        const targetOffset = relativeElementTop + scrollElement.scrollTop - safeOffset;
        scrollElement.scroll(0, targetOffset);
      } else {
        currentElement.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    }
  }

  private clearMouseTimeouts(): void {
    if (this.timeoutIdOnMouseLeave) {
      clearTimeout(this.timeoutIdOnMouseLeave);
    }

    if (this.timeoutIdOnMouseEnter) {
      clearTimeout(this.timeoutIdOnMouseEnter);
    }
  }

  public mouseEnter(): void {
    if (this.infrastructureService.isTablet) { return; }
    this.clearMouseTimeouts();

    if (!this.isSidebarOver() || this.isSidebarOpened()) {
      return;
    }

    this.timeoutIdOnMouseEnter = window.setTimeout(() => {
      void this.menuService.sidenav.open();
      this.openedOnMouseEnter = true;
      this.hideTooltips();
    }, this.delayBeforeOpen);
  }

  public mouseLeave(): void {
    if (this.infrastructureService.isTablet) { return; }
    this.clearMouseTimeouts();

    if (!this.isSidebarOver() || !this.isSidebarOpened() || !this.openedOnMouseEnter) {
      return;
    }

    this.timeoutIdOnMouseLeave = window.setTimeout(() => {
      void this.menuService.sidenav.close();
      this.openedOnMouseEnter = false;
      this.hideTooltips();
      this.changeDetector.markForCheck();
    }, 400);
  }

  public handleMenuClick($event: MouseEvent, menuItem: MenuItemNode): boolean {
    if (menuItem.$active) {
      $event.preventDefault();
      return false;
    }
    if ($event.metaKey || $event.ctrlKey) {
      return true;
    }
    this.setActiveItem(this.navigationMenu(), menuItem.url);

    if (this.hasBackdrop) {
      void this.menuService.sidenav.close();
    }
    return true;
  }

  private hideTooltips(): void {
    this.tooltips().forEach((tooltip) => {
      tooltip.hide();
    });
  }

  public togglePinned(pinned: boolean): void {
    this.menuService.updatePinnedState(pinned);

    if (pinned) {
      this.sidenavModeChange.emit('side');
      this.hasBackdrop = false;
    } else {
      this.sidenavModeChange.emit('over');
      this.hasBackdrop = true;
    }

    this.changeDetector.markForCheck();
  }

  private navigate(step: number): boolean {
    if (this.isSidebarOpened()) {
      let items: HTMLElement[] = Array.from(this.document.querySelectorAll('.iris-menu-list .menu-item'));

      if (!items.length) {
        return false;
      }

      items = items.filter((item) => {
        const groupContainer: HTMLElement = item.closest('.menu-group-container');
        if (!groupContainer) {
          return true;
        }
        return !!item.closest('.menu-item-container.expanded');
      });

      const currentFocusedElement = this.document.activeElement;
      const totalItems = items.length;

      let nextIndex: number;
      let currentIndex = items.findIndex((item) => item == currentFocusedElement);

      if (currentIndex === -1) {
        nextIndex = step > 0 ? 0 : totalItems - 1;
      } else {
        currentIndex += step;
        currentIndex %= totalItems;
        nextIndex = currentIndex < 0 ? (totalItems - 1) : currentIndex;
      }

      items[nextIndex].focus();

      return true;
    }

    return false;
  }

  public toggleSubMenu(node: MenuItemNodeI): void {
    if (this.matTree()?.isExpanded(node)) {
      this.matTree()?.collapse(node);
    } else {
      this.matTree()?.expand(node);
    }

    if (node.$hash != null) {
      const globalMenu = this.storage.getItem<Record<string, boolean>>('global_menu') || {};
      globalMenu[node.$hash] = this.matTree()?.isExpanded(node);
      this.storage.setItem('global_menu', globalMenu);
    }
  }

  private isExpanded(node: MenuItemNodeI): boolean {
    if (node.$hash == null) {
      return false;
    }

    const globalMenu = this.storage.getItem<Record<string, boolean>>('global_menu') || {};
    return globalMenu[node.$hash] ?? false;
  }

  // duplicate for html
  public getUi2SubPathOrNull = (url: string): string => {
    return this.urlSerializer.getUi2SubPathOrNull(url);
  };

  private matchUrl(srcItemUrl: string, lastActiveUrl: string, srcCurrentPageUrl: string): boolean {
    if (isNil(srcItemUrl)) { return false; }

    const itemUi2SubPath = this.getUi2SubPathOrNull(srcItemUrl);
    const itemUrl = (itemUi2SubPath ?? srcItemUrl).toLowerCase();
    const currentPageUrl = srcCurrentPageUrl.toLowerCase();
    const isLastActiveUrlDefined = !isNil(lastActiveUrl);

    const isItemUrlSegmentInCurrentPageUrl = itemUrl.split('/').every(segment => currentPageUrl.split('/').includes(segment));

    return currentPageUrl.startsWith(itemUrl) && isItemUrlSegmentInCurrentPageUrl &&
      (!isLastActiveUrlDefined ||
        itemUrl === currentPageUrl ||
        (isLastActiveUrlDefined && itemUrl.length > lastActiveUrl.length));
  }

  private _deactivateMenuItems(menu: MenuItemNodeI[]): void {
    menu.forEach((item) => {
      item.$active = false;
      if (item.children?.length) {
        item.children.forEach((subItem) => {
          subItem.$active = false;
        });
      }
    });
  }

  private setActiveItem(menu: MenuItemNodeI[], currentPageUrl: string): void {
    let lastActiveItem: MenuItemNodeI;

    menu.forEach(node => {
      if (node.children?.length) {
        node.children.forEach(childItem => {
          const lastActiveUrl = lastActiveItem?.url ?? null;
          if (this.matchUrl(childItem.url, lastActiveUrl, currentPageUrl)) {
            lastActiveItem = childItem;
            lastActiveItem.$parent = node;
          }
        });
      } else {
        const lastActiveUrl = lastActiveItem?.url ?? null;
        if (this.matchUrl(node.url, lastActiveUrl, currentPageUrl)) {
          lastActiveItem = node;
        }
      }
    });

    if (lastActiveItem) {
      this._deactivateMenuItems(menu);

      lastActiveItem.$active = true;

      if (lastActiveItem.$parent) {
        lastActiveItem.$parent.$active = true;

        if (!this.matTree()?.isExpanded(lastActiveItem.$parent)) {
          this.toggleSubMenu(lastActiveItem.$parent);
        }
      }
    }
  }

  private handleEmailClientInboxCounter(): void {
    this.globalSandbox.currentProjectId$.pipe(
      filter(Boolean),
      switchMap(projectId => this.globalSandbox.currentProject$.pipe(
        filter(project => project?.id === projectId),
        take(1),
      )),
      switchMap(project => {
        if (!project.sharedMailbox) {
          return of(false);
        }
        this.emailsWebsocketService.subscribeToUser(project.sharedMailbox);
        return this.emailsGlobalSandbox.checkUserAccessToSharedMailbox().pipe(
          filter(Boolean),
          tap(() => this.emailsGlobalSandbox.getUnreadCount()),
        );
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();
  }

  public getHighlightedTitle(title: string): HighlightedTitlePart[] {
    const searchTerm = this.searchTerm().trim();

    if (!searchTerm) {
      return [{ text: title, match: false }];
    }

    const parts: HighlightedTitlePart[] = [];
    const normalizedTitle = title.toLowerCase();
    const normalizedSearchTerm = searchTerm.toLowerCase();
    let currentIndex = 0;
    let matchIndex = normalizedTitle.indexOf(normalizedSearchTerm);

    while (matchIndex !== -1) {
      if (matchIndex > currentIndex) {
        parts.push({
          text: title.slice(currentIndex, matchIndex),
          match: false,
        });
      }

      const matchEnd = matchIndex + searchTerm.length;

      parts.push({
        text: title.slice(matchIndex, matchEnd),
        match: true,
      });

      currentIndex = matchEnd;
      matchIndex = normalizedTitle.indexOf(normalizedSearchTerm, currentIndex);
    }

    if (currentIndex < title.length) {
      parts.push({
        text: title.slice(currentIndex),
        match: false,
      });
    }

    return parts;
  }

  private countMenuItems(menuItems: MenuItemNodeI[]): number {
    return menuItems.reduce((count, item) => {
      return count + 1 + this.countMenuItems(item.children ?? []);
    }, 0);
  }

  private filterMenuItems(menuItems: MenuItemNodeI[], searchTerm: string): MenuItemNodeI[] {
    return menuItems.reduce<MenuItemNodeI[]>((filteredItems, item) => {
      const titleMatches = item.$title.toLowerCase().includes(searchTerm);
      const filteredChildren = this.filterMenuItems(item.children ?? [], searchTerm);

      if (!titleMatches && filteredChildren.length === 0) {
        return filteredItems;
      }

      if (!item.children?.length) {
        filteredItems.push(item);
        return filteredItems;
      }

      const filteredItem = Object.create(item) as MenuItemNodeI;
      // filteredItem.children = filteredChildren;

      if (titleMatches && filteredChildren.length === 0) {
        filteredItem.children = item.children;
        this.titleOnlySearchMatches.add(filteredItem);
      } else {
        filteredItem.children = filteredChildren;
      }

      filteredItems.push(filteredItem);

      return filteredItems;
    }, []);
  }

  private expandFilteredNode(node: MenuItemNodeI): void {
    if (!node.children?.length) {
      return;
    }

    if (this.titleOnlySearchMatches.has(node)) {
      this.matTree()?.collapse(node);
      return;
    }

    this.matTree()?.expand(node);
    node.children.forEach(child => this.expandFilteredNode(child));
  }

  public openSearch(): void {
    void this.menuService.sidenav.open();
  }

  public handleSearchEscape(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.searchControl.value) {
      this.searchControl.setValue('');
      return;
    }

    void this.menuService.sidenav.close();
  }
}
